import React from 'react'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { TRACK_PROPS, TrackContentType } from '@mc/track-event'
import { TrackingParamsProvider } from '@mc/tracking-context'
import {
  BaseVideo,
  Video,
  VideoControls,
  VideoControlsSmall,
  VideoProps,
} from '@mc/video'

const client = new QueryClient()

interface Props extends Partial<VideoProps> {
  contentId?: string | number
  contentUuid?: string
  parentUuid?: string
  contentType: TrackContentType
  contentTitle: string
  courseId?: string | number
  customLoader?: React.ReactNode
  extraTrackingParams?: Record<string, unknown>
  children?: React.ReactNode
  mediaUuid: string | undefined
}

export const VideoPlayer = ({
  children,
  contentId,
  contentUuid,
  parentUuid,
  contentType,
  contentTitle,
  courseId,
  extraTrackingParams,
  customLoader,
  ...rest
}: Props) => (
  <QueryClientProvider client={client}>
    <TrackingParamsProvider
      value={{
        [TRACK_PROPS.CONTENT_ID]: contentId,
        [TRACK_PROPS.CONTENT_UUID]: contentUuid,
        [TRACK_PROPS.PARENT_UUID]: parentUuid,
        [TRACK_PROPS.CONTENT_TITLE]: contentTitle,
        [TRACK_PROPS.CONTENT_TYPE]: contentType,
        [TRACK_PROPS.COURSE_ID]: courseId,
        ...extraTrackingParams,
      }}
    >
      <Video {...rest}>
        <VideoControls customLoader={customLoader} />
        {children}
      </Video>
    </TrackingParamsProvider>
  </QueryClientProvider>
)

export const VideoPlayerSmall = ({
  children,
  contentId,
  contentType,
  contentTitle,
  courseId,
  extraTrackingParams,
  ...rest
}: Props) => (
  <QueryClientProvider client={client}>
    <TrackingParamsProvider
      value={{
        [TRACK_PROPS.CONTENT_ID]: contentId,
        [TRACK_PROPS.CONTENT_TITLE]: contentTitle,
        [TRACK_PROPS.CONTENT_TYPE]: contentType,
        [TRACK_PROPS.COURSE_ID]: courseId,
        ...extraTrackingParams,
      }}
    >
      <Video {...rest}>
        <VideoControlsSmall />
        {children}
      </Video>
    </TrackingParamsProvider>
  </QueryClientProvider>
)

export const VideoPlayerNoTracking = ({
  children,
  contentId,
  contentTitle,
  contentType,
  courseId,
  extraTrackingParams,
  ...rest
}: Props) => (
  <QueryClientProvider client={client}>
    {/* These are consumed by Mux, even though Segment tracking is disabled */}
    <TrackingParamsProvider
      value={{
        [TRACK_PROPS.CONTENT_ID]: contentId,
        [TRACK_PROPS.CONTENT_TITLE]: contentTitle,
        [TRACK_PROPS.CONTENT_TYPE]: contentType,
        [TRACK_PROPS.COURSE_ID]: courseId,
        ...extraTrackingParams,
      }}
    >
      <BaseVideo {...rest}>{children}</BaseVideo>
    </TrackingParamsProvider>
  </QueryClientProvider>
)
